import { FC, useEffect, useMemo, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import {
    VStack,
    Input,
    useToast,
    HStack,
    Checkbox,
    Flex,
    Box,
    Switch,
    Divider,
    Text,
} from "@chakra-ui/react";
import {
    InternshipSchedule as Entity,
    InternshipScheduleApi as EntityApi,
    Internship,
    InternshipApi,
    TimeSlot,
    TimeSlots,
    Training,
    TrainingYear,
    TrainingYearApi,
} from "../../../apis";
import { Controller, useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import {
    AppDatePicker,
    AppFormControl,
    AppInternshipWeekSchedule,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { APPC, UTIL } from "../../../config";

const readonlyAppDatePickerStyles = {
    opacity: 1,
    bg: "var(--chakra-colors-greyT95)",
    borderColor: "var(--chakra-colors-greyT90)",
    color: "var(--chakra-colors-greyT80)",
    pointerEvents: "none",
    _hover: {
        opacity: 1,
        bg: "var(--chakra-colors-greyT95)",
        borderColor: "var(--chakra-colors-greyT90)",
        color: "var(--chakra-colors-greyT80)",
    },
    _focusVisible: {
        borderColor: "var(--chakra-colors-greyT90)",
    },
};
const readonlyAppSelectStyles = {
    control: (baseStyles) => ({
        ...baseStyles,
        pointerEvents: "none",
        borderColor: "var(--chakra-colors-greyT90)",
        minHeight: "41px",
        color: "var(--chakra-colors-greyT80)",
        backgroundColor: "var(--chakra-colors-greyT95)",
    }),
    singleValue: (baseStyles) => ({
        ...baseStyles,
        color: "var(--chakra-colors-greyT80)",
    }),
    placeholder: (baseStyles) => ({
        ...baseStyles,
        color: "var(--chakra-colors-greyT80)",
    }),
    dropdownIndicator: (baseStyles) => ({
        ...baseStyles,
        color: "var(--chakra-colors-greyT80)",
    }),
};
const readonlyCheckboxStyles = {
    ".chakra-checkbox__control": {
        opacity: 1,
        bg: "var(--chakra-colors-greyT95)",
        borderColor: "var(--chakra-colors-greyT90)",
        pointerEvents: "none",
        _hover: {
            opacity: 1,
            borderColor: "var(--chakra-colors-greyT90)",
            color: "var(--chakra-colors-greyT80)",
        },
        _checked: {
            opacity: 1,
            bg: "var(--chakra-colors-greyT50)",
        },
    },
    ".chakra-checkbox__label": {
        color: "var(--chakra-colors-greyT80)",
    },
};

export const InternshipScheduleForm: FC<{
    formContainerState: FormContainerStateReturnType;
    internshipId: string;
}> = ({ formContainerState, internshipId }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const dayjs = UTIL.getDayJs();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());
    const [selectedWeek, setSelectedWeek] = useState<AppSelectOption | null>(
        null
    );
    const [isScheduleDetail, setIsScheduleDetail] = useState(false);
    const [timeSlots, setTimeSlots] = useState<TimeSlots>([]);
    const [totalUsedSeats, setTotalUsedSeats] = useState(0);

    const [loadingTrainingYear, setLoadingTrainingYear] = useState(true);
    const [trainingYearOptions, setTrainingYearOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedTrainingYear, setSelectedTrainingYear] =
        useState<AppSelectOption | null>(null);

    const formSchema = useMemo(() => {
        const baseSchema = schema(t);
        const capacity = yup.reach(baseSchema, "capacity") as yup.NumberSchema;

        return baseSchema.shape({
            capacity: capacity
                .min(
                    totalUsedSeats,
                    t("ent.InternshipSchedule:capacity.val.minNumber", {
                        totalUsedSeats: totalUsedSeats,
                    })
                )
                .required(t("cmn:val.required")),
        });
    }, [totalUsedSeats]);

    const {
        handleSubmit,
        register,
        reset,
        setValue,
        trigger,
        getValues,
        control,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(formSchema),
        mode: "onSubmit",
    });

    // const [startDate, endDate, startTime, endTime] = useWatch({
    //     control,
    //     name: ["startDate", "endDate", "startTime", "endTime"],
    //     defaultValue: {
    //         startDate: data.startDate,
    //         endDate: data.endDate,
    //         startTime: data.startTime,
    //         endTime: data.endTime,
    //     },
    // });
    const startDate = useWatch({
        control,
        name: "startDate",
        defaultValue: data.startDate,
    });
    const endDate = useWatch({
        control,
        name: "endDate",
        defaultValue: data.endDate,
    });
    const startTime = useWatch({
        control,
        name: "startTime",
        defaultValue: data.startTime,
    });
    const endTime = useWatch({
        control,
        name: "endTime",
        defaultValue: data.endTime,
    });

    const isScheduleDetailDisabled = !(
        startDate &&
        endDate &&
        startTime &&
        endTime
    );

    // handler
    const onSubmitHandler = (formData: Entity) => {
        setLoading(true);
        formData.internship = InternshipApi.toResourceIRI(internshipId);
        formData.capacity = Number(formData.capacity);
        if (formData.timeSlots) {
            formData.timeSlots = parseTimeSlots(formData?.timeSlots);
        }
        formData.startDate = dayjs(formData.startDate).format(
            APPC.APP_HTML_DATE_FORMAT
        );
        formData.endDate = dayjs(formData.endDate).format(
            APPC.APP_HTML_DATE_FORMAT
        );
        formData.startTime = dayjs(formData.startTime).format(
            APPC.APP_HTML_TIME_FORMAT
        );
        formData.endTime = dayjs(formData.endTime).format(
            APPC.APP_HTML_TIME_FORMAT
        );

        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadTrainingYearOptions = () => {
        setLoadingTrainingYear(true);
        if (internshipId) {
            InternshipApi.getItem<Internship>(internshipId).then(
                ({ errorMessage, isNotFound, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (response !== null) {
                        const params = {
                            [`order[name]`]: "asc",
                            pagination: false,
                            "training.id": (
                                (response as Internship)?.training as Training
                            )?.id,
                        };

                        TrainingYearApi.getCollectionForSelect<TrainingYear>(
                            1,
                            params
                        )
                            .then(({ response: yearResponse }) => {
                                if (yearResponse !== null) {
                                    const options = yearResponse.items.map(
                                        (trainingYear) => ({
                                            value: TrainingYearApi.toResourceIRI(
                                                trainingYear.id
                                            ),
                                            label: `${trainingYear.code} - ${trainingYear.year}`,
                                        })
                                    );
                                    setTrainingYearOptions(options);
                                }
                            })
                            .finally(() => setLoadingTrainingYear(false));
                    }
                }
            );
        }
    };

    const calculateEndDate = (
        startDate: Date | null | string,
        week: number
    ) => {
        const newEndDate = dayjs(startDate)
            .add(week * 7 - 1, "day")
            .toDate();
        setValue("endDate", newEndDate);
        trigger("endDate");
    };

    const parseTimeSlots = (timeSlots: Record<string, any>): TimeSlots => {
        const sortByDate = (data: Record<string, any>) => {
            return Object.fromEntries(
                Object.entries(data)
                    .sort(([a], [b]) => dayjs(a).valueOf() - dayjs(b).valueOf())
                    .map(([key, value]) => [
                        key,
                        {
                            ...value,
                            startTime: `1970-01-01T${dayjs(
                                value.startTime
                            ).format(APPC.APP_HTML_TIME_FORMAT)}+00:00`,
                            endTime: `1970-01-01T${dayjs(value.endTime).format(
                                APPC.APP_HTML_TIME_FORMAT
                            )}+00:00`,
                        },
                    ])
            );
        };
        const sortedData = sortByDate(timeSlots);
        const schedule: TimeSlots = [];
        let weekDays: TimeSlot[] = [];
        let currentWeek = 0;
        Object.keys(sortedData).forEach((slot) => {
            weekDays.push(sortedData[slot]);
            if (dayjs(slot).isoWeekday() === 7) {
                schedule[currentWeek] = weekDays;
                currentWeek++;
                weekDays = [];
            }
        });
        if (weekDays.length > 0) {
            schedule[currentWeek] = weekDays;
        }
        return schedule;
    };

    const generateWeekWiseSchedule = () => {
        const schedule: TimeSlots = [];
        let currentWeek = 0;
        const currentFormValues = getValues();
        let currentDate = dayjs(currentFormValues.startDate);
        const lastDate = dayjs(currentFormValues.endDate);
        let weekDays: TimeSlot[] = [];
        const tmsKeys = currentFormValues.timeSlots
            ? Object.keys(currentFormValues.timeSlots)
            : [];
        const ts = [];
        while (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate)) {
            if (
                tmsKeys.includes(
                    dayjs(currentDate).format(APPC.APP_HTML_DATE_FORMAT)
                )
            ) {
                weekDays.push(
                    currentFormValues.timeSlots[
                        `${dayjs(currentDate).format(APPC.APP_HTML_DATE_FORMAT)}`
                    ]
                );
                ts[dayjs(currentDate).format(APPC.APP_HTML_DATE_FORMAT)] =
                    currentFormValues.timeSlots[
                        `${dayjs(currentDate).format(APPC.APP_HTML_DATE_FORMAT)}`
                    ];
            } else {
                const dayKey = UTIL.getWeekDayName(currentDate.format("d"));
                const sd = {
                    date: currentDate.format(APPC.APP_HTML_DATE_FORMAT),
                    day: dayKey,
                    startTime: currentFormValues.startTime,
                    endTime: currentFormValues.endTime,
                    breakDuration: currentFormValues?.breakDuration as number,
                    isChecked: currentFormValues?.weekDays[dayKey] ?? false,
                };
                weekDays.push(sd);
                ts[dayjs(currentDate).format(APPC.APP_HTML_DATE_FORMAT)] = sd;
            }

            if (currentDate.isoWeekday() === 7) {
                schedule[currentWeek] = weekDays;
                currentWeek++;
                weekDays = [];
            }
            currentDate = currentDate.add(1, "day");
        }
        if (weekDays.length > 0) {
            schedule[currentWeek] = weekDays;
        }
        setValue("timeSlots", ts);
        setTimeSlots(schedule);
    };

    // useEffect
    useEffect(() => {
        loadTrainingYearOptions();
    }, []);
    useEffect(() => {
        if (!formContainerState.entityId) {
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setTotalUsedSeats(response?.totalUsedSeats as number);
                    if (
                        response.trainingYear &&
                        typeof response.trainingYear !== "string"
                    ) {
                        setSelectedTrainingYear({
                            value: response.trainingYear?.["@id"],
                            label: `${response.trainingYear?.code} - ${response.trainingYear?.year}`,
                        });
                    }
                    setData(response);
                    if (response.timeSlots) {
                        setTimeSlots(response.timeSlots);
                    }
                    setIsScheduleDetail(response.isScheduleDetail);
                    setValue(
                        "startDate",
                        UTIL.toFixedUtcDate(response.startDate)
                    );
                    setValue("endDate", UTIL.toFixedUtcDate(response.endDate));
                    setValue(
                        "startTime",
                        UTIL.toFixedUtcTime(response.startTime)
                    );
                    setValue("endTime", UTIL.toFixedUtcTime(response.endTime));
                }
            })
            .finally(() => setLoading(false));

        return () => {
            reset();
            setData(new Entity());
        };
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t("padm.InternshipScheduleForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size={isScheduleDetail ? "full" : "lg"}
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <Flex gap={6} flexWrap={"wrap"} w={"full"}>
                    <VStack
                        gap={5}
                        alignItems={"stretch"}
                        justifyContent={"flex-start"}
                        w={"full"}
                        maxW={isScheduleDetail ? "470px" : "full"}
                    >
                        <HStack gap={2} w={"full"} alignItems={"flex-start"}>
                            <Box flexGrow={2} flexBasis={2}>
                                <AppFormControl
                                    label={t(
                                        "ent.InternshipSchedule:name.label"
                                    )}
                                    isInvalid={!!errors.name}
                                    message={errors.name?.message}
                                    isLoading={loading}
                                >
                                    <Input
                                        type="text"
                                        placeholder={t(
                                            "ent.InternshipSchedule:name.placeholder"
                                        )}
                                        {...register("name")}
                                        defaultValue={data.name}
                                    />
                                </AppFormControl>
                            </Box>
                            <Box flexGrow={1} flexBasis={1}>
                                <AppFormControl
                                    label={t(
                                        "ent.InternshipSchedule:capacity.label"
                                    )}
                                    isInvalid={!!errors.capacity}
                                    message={errors.capacity?.message}
                                    isLoading={loading}
                                >
                                    <Input
                                        type="number"
                                        placeholder={t(
                                            "ent.InternshipSchedule:capacity.placeholder"
                                        )}
                                        {...register("capacity")}
                                        defaultValue={data.capacity}
                                    />
                                </AppFormControl>
                            </Box>
                        </HStack>
                        <HStack gap={2} w={"full"} alignItems={"flex-start"}>
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:startDate.label"
                                )}
                                isInvalid={!!errors.startDate}
                                message={errors.startDate?.message}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="startDate"
                                    defaultValue={UTIL.toFixedUtcDate(
                                        data.startDate
                                    )}
                                    render={({ field }) => (
                                        <AppDatePicker
                                            selected={
                                                (field.value as Date) ?? null
                                            }
                                            onChange={(date) => {
                                                field.onChange(date);
                                                if (
                                                    date &&
                                                    selectedWeek?.value
                                                ) {
                                                    calculateEndDate(
                                                        date,
                                                        selectedWeek?.value as number
                                                    );
                                                }
                                            }}
                                            placeholderText={t(
                                                "ent.InternshipSchedule:startDate.placeholder"
                                            )}
                                            dateFormat={APPC.APP_DP_DATE_FORMAT}
                                            customInput={
                                                <Input
                                                    sx={
                                                        isScheduleDetail
                                                            ? readonlyAppDatePickerStyles
                                                            : {}
                                                    }
                                                />
                                            }
                                            isClearable={!isScheduleDetail}
                                            readOnly={isScheduleDetail}
                                        />
                                    )}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.InternshipSchedule:weeks.label")}
                                isLoading={loading}
                            >
                                <AppSelect<AppSelectOption>
                                    name="weeks"
                                    isClearable={!isScheduleDetail}
                                    isLoading={loading}
                                    options={[...Array(15)].map((_, index) => ({
                                        value: index + 1,
                                        label: String(index + 1),
                                    }))}
                                    value={selectedWeek}
                                    placeholder={t(
                                        "ent.InternshipSchedule:weeks.placeholder"
                                    )}
                                    onChange={(e) => {
                                        setSelectedWeek(e as AppSelectOption);
                                        const startDate =
                                            getValues("startDate");
                                        if (startDate && e?.value) {
                                            calculateEndDate(
                                                startDate,
                                                e?.value as number
                                            );
                                        }
                                    }}
                                    {...(isScheduleDetail
                                        ? { menuIsOpen: false }
                                        : {})}
                                    styles={
                                        isScheduleDetail
                                            ? readonlyAppSelectStyles
                                            : {}
                                    }
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:endDate.label"
                                )}
                                isInvalid={!!errors.endDate}
                                message={errors.endDate?.message}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="endDate"
                                    defaultValue={UTIL.toFixedUtcDate(
                                        data.endDate
                                    )}
                                    render={({ field }) => (
                                        <AppDatePicker
                                            selected={
                                                (field.value as Date) ?? null
                                            }
                                            onChange={(date) => {
                                                field.onChange(date);
                                            }}
                                            placeholderText={t(
                                                "ent.InternshipSchedule:endDate.placeholder"
                                            )}
                                            dateFormat={APPC.APP_DP_DATE_FORMAT}
                                            customInput={
                                                <Input
                                                    sx={
                                                        isScheduleDetail
                                                            ? readonlyAppDatePickerStyles
                                                            : {}
                                                    }
                                                />
                                            }
                                            isClearable={!isScheduleDetail}
                                            readOnly={isScheduleDetail}
                                        />
                                    )}
                                />
                            </AppFormControl>
                        </HStack>
                        <HStack gap={2} w={"full"} alignItems={"flex-start"}>
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:startTime.label"
                                )}
                                isInvalid={!!errors.startTime}
                                message={errors.startTime?.message}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="startTime"
                                    defaultValue={
                                        typeof data.startTime === "string"
                                            ? UTIL.toFixedUtcTime(
                                                  data.startTime
                                              )
                                            : null
                                    }
                                    render={({ field }) => (
                                        <AppDatePicker
                                            selected={
                                                UTIL.isValidDate(field.value)
                                                    ? field.value
                                                    : null
                                            }
                                            onChange={(date) => {
                                                field.onChange(date);
                                            }}
                                            placeholderText={t(
                                                "ent.InternshipSchedule:startTime.placeholder"
                                            )}
                                            showTimeSelect
                                            showTimeSelectOnly
                                            timeIntervals={60}
                                            timeCaption={t("cmn:label.time")}
                                            dateFormat={APPC.APP_DP_TIME_FORMAT}
                                            timeFormat={APPC.APP_DP_TIME_FORMAT}
                                            customInput={
                                                <Input
                                                    sx={
                                                        isScheduleDetail
                                                            ? readonlyAppDatePickerStyles
                                                            : {}
                                                    }
                                                />
                                            }
                                            isClearable={!isScheduleDetail}
                                            readOnly={isScheduleDetail}
                                        />
                                    )}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:endTime.label"
                                )}
                                isInvalid={!!errors.endTime}
                                message={errors.endTime?.message}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="endTime"
                                    defaultValue={
                                        typeof data.endTime === "string"
                                            ? UTIL.toFixedUtcTime(data.endTime)
                                            : null
                                    }
                                    render={({ field }) => (
                                        <AppDatePicker
                                            selected={
                                                UTIL.isValidDate(field.value)
                                                    ? field.value
                                                    : null
                                            }
                                            onChange={(date) => {
                                                field.onChange(date);
                                            }}
                                            placeholderText={t(
                                                "ent.InternshipSchedule:endTime.placeholder"
                                            )}
                                            showTimeSelect
                                            showTimeSelectOnly
                                            timeIntervals={60}
                                            timeCaption={t("cmn:label.time")}
                                            dateFormat={APPC.APP_DP_TIME_FORMAT}
                                            timeFormat={APPC.APP_DP_TIME_FORMAT}
                                            customInput={
                                                <Input
                                                    sx={
                                                        isScheduleDetail
                                                            ? readonlyAppDatePickerStyles
                                                            : {}
                                                    }
                                                />
                                            }
                                            isClearable={!isScheduleDetail}
                                            readOnly={isScheduleDetail}
                                        />
                                    )}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:breakDuration.label"
                                )}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="breakDuration"
                                    defaultValue={data.breakDuration}
                                    render={({ field }) => (
                                        <AppSelect<AppSelectOption>
                                            name={field.name}
                                            isLoading={loading}
                                            options={APPC.FE.InternshipSchedule.BREAK_DURATION.map(
                                                (br) => ({
                                                    value: br,
                                                    label: t(
                                                        `con.InternshipSchedule:${br}`
                                                    ),
                                                })
                                            )}
                                            value={{
                                                value: field.value,
                                                label: t(
                                                    `con.InternshipSchedule:${field.value}`
                                                ),
                                            }}
                                            placeholder={t(
                                                "ent.InternshipSchedule:breakDuration.placeholder"
                                            )}
                                            onChange={(e) =>
                                                field.onChange(e?.value || 0)
                                            }
                                            {...(isScheduleDetail
                                                ? { menuIsOpen: false }
                                                : {})}
                                            styles={
                                                isScheduleDetail
                                                    ? readonlyAppSelectStyles
                                                    : {}
                                            }
                                        />
                                    )}
                                />
                            </AppFormControl>
                        </HStack>
                        <HStack
                            gap={2}
                            w={"full"}
                            justifyContent={"space-between"}
                        >
                            <AppFormControl
                                label={t(
                                    "ent.InternshipSchedule:trainingYear.label"
                                )}
                                isInvalid={!!errors.trainingYear}
                                message={errors.trainingYear?.message}
                                isLoading={loading}
                            >
                                <Controller
                                    control={control}
                                    name="trainingYear"
                                    defaultValue={
                                        selectedTrainingYear?.value as string
                                    }
                                    render={({ field }) => (
                                        <AppSelect<AppSelectOption>
                                            name={field.name}
                                            isClearable
                                            isLoading={loadingTrainingYear}
                                            options={trainingYearOptions}
                                            value={selectedTrainingYear || null}
                                            placeholder={t(
                                                "ent.InternshipSchedule:trainingYear.placeholder"
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e?.value || null
                                                );
                                                setSelectedTrainingYear(
                                                    e as AppSelectOption
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </AppFormControl>
                            <AppFormControl isLoading={loading}>
                                <Flex
                                    alignItems={"center"}
                                    justifyContent={"end"}
                                    gap={4}
                                    mt={8}
                                >
                                    <Text textStyle={"regularMd"}>
                                        {t(
                                            "ent.InternshipSchedule:isBreakInclude.label"
                                        )}
                                    </Text>
                                    <Switch
                                        {...register("isBreakInclude")}
                                        defaultChecked={data?.isBreakInclude}
                                    />
                                </Flex>
                            </AppFormControl>
                        </HStack>
                        <AppFormControl
                            label={t("ent.InternshipSchedule:weekDays.label")}
                            isLoading={loading}
                        >
                            <HStack gap={4}>
                                {Object.entries(data?.weekDays || {}).map(
                                    ([day, value]) => (
                                        <Checkbox
                                            key={day}
                                            defaultChecked={value}
                                            {...register(`weekDays.${day}`)}
                                            isReadOnly={isScheduleDetail}
                                            sx={
                                                isScheduleDetail
                                                    ? readonlyCheckboxStyles
                                                    : {}
                                            }
                                        >
                                            {t(`cmn:label.${day}`)}
                                        </Checkbox>
                                    )
                                )}
                            </HStack>
                        </AppFormControl>
                        <HStack
                            gap={2}
                            w={"full"}
                            justifyContent={"space-between"}
                        >
                            <></>
                            <AppFormControl isLoading={loading}>
                                <Flex
                                    alignItems={"center"}
                                    justifyContent={"end"}
                                    gap={4}
                                >
                                    <Text
                                        textStyle={"regularMd"}
                                        color={
                                            isScheduleDetailDisabled
                                                ? "var(--chakra-colors-greyT80)"
                                                : ""
                                        }
                                    >
                                        {t(
                                            "ent.InternshipSchedule:isScheduleDetail.label"
                                        )}
                                    </Text>
                                    <Switch
                                        {...register("isScheduleDetail")}
                                        defaultChecked={isScheduleDetail}
                                        onChange={(e) => {
                                            setIsScheduleDetail(
                                                e.target.checked
                                            );
                                            if (e.target.checked) {
                                                generateWeekWiseSchedule();
                                            }
                                        }}
                                        disabled={
                                            isScheduleDetail
                                                ? false
                                                : isScheduleDetailDisabled
                                        }
                                    />
                                </Flex>
                            </AppFormControl>
                        </HStack>
                    </VStack>
                    {isScheduleDetail && (
                        <Divider
                            orientation="vertical"
                            h="auto"
                            borderColor={"var(--chakra-colors-greyT80)"}
                        />
                    )}
                    {isScheduleDetail && timeSlots.length > 0 && (
                        <Flex gap={4} flexWrap={"wrap"} minW="476px" flex={1}>
                            <AppInternshipWeekSchedule<Entity>
                                timeSlots={timeSlots}
                                register={register}
                                control={control}
                                loading={loading}
                            />
                        </Flex>
                    )}
                </Flex>
            </form>
        </AppFormContainer>
    );
};
